import {
  DEFAULT_COUNTRY_CODE,
  resolveCountryCodeByLanguage,
  type CountryCode,
} from '../../../shared/config';
import {
  formatAmountValue,
  formatCurrency,
  resolveCurrencyPrefix,
} from '../../../shared/model';

export function resolveTipCheckoutCountryCode(language: string): CountryCode {
  return resolveCountryCodeByLanguage(language);
}

export function resolveTipCheckoutCurrencyPrefix(
  countryCode: CountryCode = DEFAULT_COUNTRY_CODE,
): string {
  return resolveCurrencyPrefix(countryCode);
}
export const TIP_CHECKOUT_PRESET_AMOUNTS = [10, 30, 50, 100, 300] as const;
export const TIP_CHECKOUT_MIN_AMOUNT = 3;
export const TIP_CHECKOUT_DEFAULT_AMOUNT = 50;
export const TIP_CHECKOUT_MAX_AMOUNT = 5000;
export const TIP_CHECKOUT_SHEET_CLOSE_ANIMATION_MS = 240;
export const TIP_CHECKOUT_PROVIDER_FEE_RATE = 0.05;
export const TIP_CHECKOUT_STAR_VALUES = [1, 2, 3, 4, 5] as const;
export const TIP_CHECKOUT_FEEDBACK_MAX_LENGTH = 400;
export const TIP_CHECKOUT_FEEDBACK_PREFILL = '';
export const TIP_CHECKOUT_PROFILE_RATING_VALUE = 4.8;
export const TIP_CHECKOUT_FEEDBACK_PROMPT_KEYS = {
  improve: 'tipsCheckout.feedbackPromptImprove',
  negative: 'tipsCheckout.feedbackPromptNegative',
  positive: 'tipsCheckout.feedbackPromptPositive',
} as const;
const TIP_CHECKOUT_PAYER_CONTACT_SKIP_CTA_KEYS: Record<CountryCode, string> = {
  '+54': 'tipsCheckout.payerContactSkipCtaArgentina',
  '+55': 'tipsCheckout.payerContactSkipCtaBrazil',
};
const TIP_CHECKOUT_PAYER_CONTACT_SKIP_HINT_KEYS: Record<CountryCode, string> = {
  '+54': 'tipsCheckout.payerContactSkipHintArgentina',
  '+55': 'tipsCheckout.payerContactSkipHintBrazil',
};

export type TipCheckoutRatingValue = (typeof TIP_CHECKOUT_STAR_VALUES)[number];

export type TipCheckoutPaymentMethod =
  | 'visa'
  | 'mastercard'
  | 'elo'
  | 'pix'
  | 'tether';
export type TipCheckoutPayoutProvider =
  | 'itau'
  | 'nubank'
  | 'pra-voce'
  | 'usdt-bep20'
  | 'usdt-trc20'
  | 'new-card';

export const TIP_CHECKOUT_PAYMENT_METHODS: readonly TipCheckoutPaymentMethod[] =
  ['visa', 'mastercard', 'elo', 'pix', 'tether'];
export const TIP_CHECKOUT_PAYOUT_PROVIDERS: readonly TipCheckoutPayoutProvider[] =
  ['itau', 'nubank', 'pra-voce', 'usdt-bep20', 'usdt-trc20', 'new-card'];
// Brand and protocol labels are identical across locales, so duplicating them in
// translation files adds maintenance noise without improving UX.
export const TIP_CHECKOUT_PAYOUT_PROVIDER_LABELS: Record<
  Exclude<TipCheckoutPayoutProvider, 'new-card'>,
  string
> = {
  itau: 'Itaú',
  nubank: 'Nubank',
  'pra-voce': 'Pra Você',
  'usdt-bep20': 'USDT (BEP-20)',
  'usdt-trc20': 'USDT (TRC-20)',
};

export function formatTipCheckoutAmountValue(
  amount: number,
  countryCode: CountryCode = DEFAULT_COUNTRY_CODE,
): string {
  return formatAmountValue(amount, countryCode);
}

export function formatTipCheckoutCurrencyAmount(
  amount: number,
  countryCode: CountryCode = DEFAULT_COUNTRY_CODE,
): string {
  return formatCurrency(amount, countryCode);
}

export function clampTipCheckoutAmount(amount: number | null): number | null {
  if (amount === null || !Number.isFinite(amount) || amount < 0) {
    return null;
  }

  return Math.min(Math.floor(amount), TIP_CHECKOUT_MAX_AMOUNT);
}

export function isTipCheckoutPayableAmount(
  amount: number | null,
): amount is number {
  return (
    amount !== null &&
    Number.isFinite(amount) &&
    amount >= TIP_CHECKOUT_MIN_AMOUNT &&
    amount <= TIP_CHECKOUT_MAX_AMOUNT
  );
}

export function getTipCheckoutFeedbackPromptKey(ratingValue: number | null) {
  if (
    !Number.isFinite(ratingValue) ||
    ratingValue === null ||
    ratingValue <= 0
  ) {
    return null;
  }

  if (ratingValue <= 2) {
    return TIP_CHECKOUT_FEEDBACK_PROMPT_KEYS.negative;
  }

  if (ratingValue <= 4) {
    return TIP_CHECKOUT_FEEDBACK_PROMPT_KEYS.improve;
  }

  return TIP_CHECKOUT_FEEDBACK_PROMPT_KEYS.positive;
}

export function resolveTipCheckoutPayerContactSkipCtaKey(
  countryCode: CountryCode,
) {
  return TIP_CHECKOUT_PAYER_CONTACT_SKIP_CTA_KEYS[countryCode];
}

export function resolveTipCheckoutPayerContactSkipHintKey(
  countryCode: CountryCode,
) {
  return TIP_CHECKOUT_PAYER_CONTACT_SKIP_HINT_KEYS[countryCode];
}

export function getTipCheckoutRatingFillLevels(ratingValue: number): number[] {
  return TIP_CHECKOUT_STAR_VALUES.map((_, index) => {
    if (!Number.isFinite(ratingValue) || ratingValue <= 0) {
      return 0;
    }

    return Math.max(0, Math.min(1, ratingValue - index));
  });
}
